import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Send,
  Clock,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Shield,
  Bug,
  HelpCircle,
  Lightbulb,
  MoreHorizontal,
  RefreshCw,
  Info
} from 'lucide-react';
import './Tickets.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const STATUS_CONFIG = {
  IN_PROGRESS: { label: 'En cours', color: '#f59e0b', icon: Clock, bgColor: '#fffbeb' },
  RESOLVED: { label: 'Résolu', color: '#10b981', icon: CheckCircle, bgColor: '#ecfdf5' },
  CLOSED: { label: 'Fermé', color: '#6b7280', icon: XCircle, bgColor: '#f3f4f6' }
};

const CATEGORY_CONFIG = {
  BUG: { label: 'Bug', icon: Bug, color: '#ef4444' },
  QUESTION: { label: 'Question', icon: HelpCircle, color: '#3b82f6' },
  SUGGESTION: { label: 'Suggestion', icon: Lightbulb, color: '#f59e0b' },
  OTHER: { label: 'Autre', icon: MoreHorizontal, color: '#6b7280' }
};

const PRIORITY_CONFIG = {
  LOW: { label: 'Basse', color: '#6b7280' },
  MEDIUM: { label: 'Moyenne', color: '#3b82f6' },
  HIGH: { label: 'Haute', color: '#f59e0b' },
  CRITICAL: { label: 'Critique', color: '#ef4444' }
};

function TicketDetail({ ticket: initialTicket, token, user, onBack, onTicketUpdated }) {
  const [ticket, setTicket] = useState(initialTicket);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTicketDetails();
  }, [initialTicket.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tickets/${initialTicket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTicket(response.data.ticket);
        setMessages(response.data.ticket.messages || []);
        setIsAdmin(response.data.isAdmin);
      }
      setError(null);
    } catch (err) {
      console.error('Erreur chargement ticket:', err);
      setError('Impossible de charger le ticket');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    // Use block: 'nearest' to only scroll within the messages container, not the entire page
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      const response = await axios.post(
        `${API_URL}/api/tickets/${ticket.id}/messages`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setMessages(prev => [...prev, response.data.message]);
        setNewMessage('');

        // Mettre à jour le statut si changé
        if (response.data.newStatus && response.data.newStatus !== ticket.status) {
          const updatedTicket = { ...ticket, status: response.data.newStatus };
          setTicket(updatedTicket);
          onTicketUpdated(updatedTicket);
        }
      }
    } catch (err) {
      console.error('Erreur envoi message:', err);
      setError('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setStatusChanging(true);
      setError(null);

      const response = await axios.patch(
        `${API_URL}/api/tickets/${ticket.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const updatedTicket = { ...ticket, status: newStatus };
        setTicket(updatedTicket);
        onTicketUpdated(updatedTicket);
        setShowCloseConfirm(false);
      }
    } catch (err) {
      console.error('Erreur changement statut:', err);
      const errorMessage = err.response?.data?.error || 'Erreur lors du changement de statut';
      setError(errorMessage);
    } finally {
      setStatusChanging(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.IN_PROGRESS;
  const categoryConfig = CATEGORY_CONFIG[ticket.category] || CATEGORY_CONFIG.QUESTION;
  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.MEDIUM;
  const StatusIcon = statusConfig.icon;
  const CategoryIcon = categoryConfig.icon;

  if (loading) {
    return (
      <div className="ticket-detail-loading">
        <RefreshCw size={24} className="spinning" />
        <span>Chargement du ticket...</span>
      </div>
    );
  }

  return (
    <div className="ticket-detail">
      <div className="ticket-detail-header">
        <button className="ticket-detail-back" onClick={onBack}>
          <ArrowLeft size={20} />
          Retour
        </button>

        <div className="ticket-detail-info">
          <div className="ticket-detail-number">
            Ticket #{String(ticket.number).padStart(4, '0')}
          </div>
          <div
            className="ticket-detail-status"
            style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
          >
            <StatusIcon size={14} />
            {statusConfig.label}
          </div>
        </div>

        {isAdmin && ticket.status !== 'CLOSED' && (
          <div className="ticket-detail-actions">
            {ticket.status !== 'RESOLVED' && (
              <button
                className="ticket-action-btn resolve"
                onClick={() => setShowResolveConfirm(true)}
                disabled={statusChanging}
              >
                <CheckCircle size={16} />
                Marquer résolu
              </button>
            )}
            <button
              className="ticket-action-btn close"
              onClick={() => setShowCloseConfirm(true)}
              disabled={statusChanging}
            >
              <XCircle size={16} />
              Fermer
            </button>
          </div>
        )}
      </div>

      <div className="ticket-detail-body">
        <div className="ticket-detail-main">
          <div className="ticket-detail-subject">
            <h1>{ticket.subject}</h1>
            <div className="ticket-detail-meta">
              <span style={{ color: categoryConfig.color }}>
                <CategoryIcon size={14} />
                {categoryConfig.label}
              </span>
              {isAdmin && (
                <span style={{ color: priorityConfig.color }}>
                  Priorité {priorityConfig.label}
                </span>
              )}
              <span>
                <Clock size={14} />
                {formatDate(ticket.createdAt)}
              </span>
            </div>
          </div>

          {error && (
            <div className="ticket-detail-error">
              <AlertCircle size={16} />
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          <div className="ticket-messages">
            {messages.map((msg, index) => (
              <div
                key={msg.id}
                className={`ticket-message ${msg.isFromAdmin ? 'admin' : 'user'}`}
              >
                <div className="ticket-message-avatar">
                  {msg.author.picture ? (
                    <img src={msg.author.picture} alt={msg.author.name} />
                  ) : msg.isFromAdmin ? (
                    <Shield size={20} />
                  ) : (
                    <User size={20} />
                  )}
                </div>
                <div className="ticket-message-content">
                  <div className="ticket-message-header">
                    <span className="ticket-message-author">
                      {msg.isFromAdmin ? 'Support Kokpit' : msg.author.name}
                    </span>
                    <span className="ticket-message-date">
                      {formatDate(msg.createdAt)}
                    </span>
                  </div>
                  <div className="ticket-message-text">
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {ticket.status !== 'CLOSED' ? (
            <form className="ticket-reply-form" onSubmit={handleSendMessage}>
              <textarea
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder="Écrivez votre message..."
                rows={3}
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
              >
                {sending ? (
                  <span className="loading-spinner" />
                ) : (
                  <Send size={18} />
                )}
                Envoyer
              </button>
            </form>
          ) : (
            <div className="ticket-closed-notice">
              <XCircle size={18} />
              Ce ticket est fermé et ne peut plus recevoir de messages.
            </div>
          )}
        </div>

        {isAdmin && ticket.context && (
          <div className="ticket-detail-sidebar">
            <div className="ticket-context-panel">
              <h3>
                <Info size={16} />
                Contexte
              </h3>

              <div className="ticket-context-section">
                <h4>Utilisateur</h4>
                <div className="ticket-context-item">
                  <span>Nom</span>
                  <span>{ticket.user.name}</span>
                </div>
                <div className="ticket-context-item">
                  <span>Email</span>
                  <span>{ticket.user.email}</span>
                </div>
                <div className="ticket-context-item">
                  <span>Plan</span>
                  <span>{ticket.context.userPlan || 'FREE'}</span>
                </div>
                <div className="ticket-context-item">
                  <span>Inscrit le</span>
                  <span>{ticket.user.createdAt ? new Date(ticket.user.createdAt).toLocaleDateString('fr-FR') : '-'}</span>
                </div>
                {ticket.context.userOrganization && (
                  <div className="ticket-context-item">
                    <span>Organisation</span>
                    <span>{ticket.context.userOrganization}</span>
                  </div>
                )}
              </div>

              {(ticket.context.lastVisitedPage || ticket.context.currentPage || ticket.context.selectedClientName || ticket.context.selectedAccountId) && (
                <div className="ticket-context-section">
                  <h4>Page / Sélection</h4>
                  {ticket.context.lastVisitedPage && (
                    <div className="ticket-context-item">
                      <span>Dernière page</span>
                      <span>{ticket.context.lastVisitedPage}</span>
                    </div>
                  )}
                  {ticket.context.selectedClientName && (
                    <div className="ticket-context-item">
                      <span>Client</span>
                      <span>{ticket.context.selectedClientName}</span>
                    </div>
                  )}
                  {ticket.context.selectedSiteName && (
                    <div className="ticket-context-item">
                      <span>Site</span>
                      <span>{ticket.context.selectedSiteName}</span>
                    </div>
                  )}
                  {ticket.context.selectedAccountId && (
                    <div className="ticket-context-item">
                      <span>Compte</span>
                      <span>{ticket.context.selectedAccountId}</span>
                    </div>
                  )}
                  {ticket.context.selectedPlatform && (
                    <div className="ticket-context-item">
                      <span>Plateforme</span>
                      <span>{ticket.context.selectedPlatform}</span>
                    </div>
                  )}
                </div>
              )}

              {ticket.context.connectedPlatforms && ticket.context.connectedPlatforms.length > 0 && (
                <div className="ticket-context-section">
                  <h4>Connecteurs</h4>
                  <div className="ticket-context-item">
                    <span>Plateformes</span>
                    <span>{ticket.context.connectedPlatforms.join(', ')}</span>
                  </div>
                  <div className="ticket-context-item">
                    <span>Total comptes</span>
                    <span>{ticket.context.totalAccounts}</span>
                  </div>
                </div>
              )}

              {ticket.context.screenSize && (
                <div className="ticket-context-section">
                  <h4>Technique</h4>
                  <div className="ticket-context-item">
                    <span>Écran</span>
                    <span>{ticket.context.screenSize}</span>
                  </div>
                  {ticket.context.userAgent && (
                    <div className="ticket-context-item">
                      <span>Navigateur</span>
                      <span className="truncate">{ticket.context.userAgent.substring(0, 50)}...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmation pour marquer résolu */}
      {showResolveConfirm && ReactDOM.createPortal(
        <div className="ticket-modal-overlay" onClick={() => setShowResolveConfirm(false)}>
          <div className="ticket-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="ticket-confirm-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
              <CheckCircle size={32} />
            </div>
            <h3>Marquer ce ticket comme résolu ?</h3>
            <p>
              L'utilisateur sera notifié par email que son ticket a été résolu.
              Il pourra répondre pour rouvrir le ticket si nécessaire.
            </p>
            <div className="ticket-confirm-actions">
              <button
                className="ticket-confirm-cancel"
                onClick={() => setShowResolveConfirm(false)}
                disabled={statusChanging}
              >
                Annuler
              </button>
              <button
                className="ticket-confirm-submit"
                style={{ background: '#3b82f6' }}
                onClick={() => {
                  handleStatusChange('RESOLVED');
                  setShowResolveConfirm(false);
                }}
                disabled={statusChanging}
              >
                {statusChanging ? 'En cours...' : 'Marquer résolu'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de confirmation pour fermer le ticket */}
      {showCloseConfirm && ReactDOM.createPortal(
        <div className="ticket-modal-overlay" onClick={() => setShowCloseConfirm(false)}>
          <div className="ticket-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="ticket-confirm-icon">
              <AlertTriangle size={32} />
            </div>
            <h3>Fermer ce ticket ?</h3>
            <p>
              Une fois fermé, ce ticket ne pourra plus recevoir de nouveaux messages.
              L'utilisateur sera notifié par email.
            </p>
            <div className="ticket-confirm-actions">
              <button
                className="ticket-confirm-cancel"
                onClick={() => setShowCloseConfirm(false)}
                disabled={statusChanging}
              >
                Annuler
              </button>
              <button
                className="ticket-confirm-submit"
                onClick={() => handleStatusChange('CLOSED')}
                disabled={statusChanging}
              >
                {statusChanging ? 'Fermeture...' : 'Fermer le ticket'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default TicketDetail;
